import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui';
import { PasskeyList } from '../../components/auth/PasskeyList';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { api } from '../../lib/api';
import { authApi } from '../../lib/api';
import { startRegistration } from '@simplewebauthn/browser';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/types';
import type { PasskeyInfo } from '@leal-crm/shared';
import { pageStyles, cardStyles } from '../../lib/styles';

export function PasskeysPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { success: showSuccess, error: showError, info: showInfo } = useToast();
  const [passkeys, setPasskeys] = useState<PasskeyInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);

  const fetchPasskeys = async () => {
    try {
      setIsLoading(true);
      const response = await api.get<{ passkeys: PasskeyInfo[] }>('/api/auth/passkeys');
      setPasskeys(response.passkeys);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.error');
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPasskeys();
  }, []);

  const handleAddPasskey = async () => {
    if (!user) return;

    // Check WebAuthn support
    if (!window.PublicKeyCredential) {
      showError(t('passkey.browserNotSupported'));
      return;
    }

    setIsAdding(true);
    try {
      // Start registration
      const { challengeId, options } = await authApi.registerStart(user.email);

      // Create credential
      const credential = await startRegistration({ optionsJSON: options as PublicKeyCredentialCreationOptionsJSON });

      // Finish registration
      await authApi.registerFinish(user.email, credential, challengeId);

      showSuccess(t('passkey.added'));
      fetchPasskeys();
    } catch (err) {
      if (err instanceof Error && err.name === 'NotAllowedError') {
        showInfo(t('common.cancel'));
      } else {
        const message = err instanceof Error ? err.message : t('common.error');
        showError(message);
      }
    } finally {
      setIsAdding(false);
    }
  };

  const hasPassword = user?.hasPassword ?? false;

  return (
    <div className={pageStyles.container}>
      <div className={cardStyles.container}>
        <div className={`${cardStyles.header} flex items-center justify-between`}>
          <div>
            <span className="text-xs font-medium text-gray-900">{t('passkey.title')}</span>
            <p className="mt-0.5 text-[11px] text-gray-500">
              {passkeys.length} passkey(s) configuradas
            </p>
          </div>
          <Button onClick={handleAddPasskey} loading={isAdding}>
            {t('passkey.addPasskey')}
          </Button>
        </div>
        <div className={cardStyles.content}>
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
            </div>
          ) : (
            <PasskeyList
              passkeys={passkeys}
              hasPassword={hasPassword}
              onRefresh={fetchPasskeys}
            />
          )}
        </div>
      </div>
    </div>
  );
}
